import { Fragment, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { SessionContext } from "../common/contexts/SessionContext";
import { LucideIcon } from "../common/icon/LucideIcon";
import { NotAvailable, NotSet } from "../common/misc/UIconstants";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { getAddRoute, getIndexRoute } from "../main/Routing";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { inventoriesConstants, hierarchyConstants } from "../api/Inventories";
import { CustomTypeDetailRow, CustomTypeRenderer } from "../Customization/CustomTypes/generics/CustomTypeRenderer";
import { InventoriesTile } from "../common/sidebar/tiles/InventoriesTile";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import { useFeatureFlag } from "../common/contexts/UseFeatureFlags";
import { GetProjects } from "../common/misc/EntityRenders/EntityRenderer";
import { Button } from "../common/buttons/Button/Button";
import { CustomType, customTypeConstants, CustomTypeFilters } from "../api/CustomTypes/CustomTypes";
import { CustomFieldReferencesTile } from "../common/sidebar/tiles/CustomFieldReferencesTile";
import { EntityBreadcrumbs } from "../common/hierarchy/breadcrumbs/EntityBreadcrumbs";
import { AddCustomTypeEntity } from "../Customization/CustomTypes/generics/AddCustomTypeEntity";
import { useEntityDetail, useUnpaginateOrdered } from "../api/BaseEntityApi";
import { QuickAddWrapper } from "../common/forms/QuickAdd/common/QuickAddWrapper";
import { QuickAddInventoryItemForm } from "../common/forms/QuickAdd/forms/QuickAddInventoryItemForm";
import { EntityRestoreButton } from "../common/entity/entityComponents/EntityRestoreButton";
import { EntitySidebarTileAutosorter } from "../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityDeleteButton } from "../Entities/GenericComponents/common/GenericEntityDetailPage";
import { EntityEditButton } from "../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../common/entity/entityComponents/EntityCloneButton";
import { EntityComplianceDropdown } from "../common/entity/entityComponents/EntityComplianceDropdown";
import { PrintCompatibleLink } from "../common/misc/PrintCompatibleLink.tsx/PrintCompatibleLink";
import { GenericEntityExportButton } from "../Entities/GenericComponents/common/GenericEntityExportButton";

export interface InventoryItemProps extends EntityDetailTableProps<"inventoryItems"> {
  showHierarchy?: boolean;
}

export const InventoryItemsDetailTable = ({
  entity,
  entityTypeId,
  entityConstants,
  fieldLabels,
  showHierarchy = false,
  showLinks = true,
}: InventoryItemProps) => {
  const { route } = useContext(SessionContext);
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          {!!showHierarchy && !!entity.ancestors?.length && (
            <Table.Body.RowContent
              title={fieldLabels.ancestors}
              content={
                <div
                  className="container_label flex align-center gap-5"
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={entity.ancestors.map((a) => a.name).join(" > ") + " > " + entity.name}
                >
                  {entity.ancestors.map((ancestor, index) => (
                    <Fragment key={ancestor.id}>
                      {showLinks ? (
                        <PrintCompatibleLink
                          to={`${route(getIndexRoute(entityConstants.frontendIndexRoute))}/${ancestor.id}`}
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          <LucideIcon name={entityConstants.icon} color="var(--primary)" /> {ancestor.name}
                        </PrintCompatibleLink>
                      ) : (
                        <div>
                          <LucideIcon name={entityConstants.icon} color="var(--primary)" /> {ancestor.name}
                        </div>
                      )}
                      {index < entity.ancestors.length - 1 && (
                        <div>
                          <LucideIcon name="arrow-big-right" />
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
              }
            />
          )}

          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label" title={entity.name}>
                <div
                  className="container_label_name"
                  style={{
                    fontWeight: 600,
                    paddingLeft: !!showHierarchy && !!entity.ancestors.length ? "5px" : undefined,
                  }}
                >
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </span>
                </div>
              </div>
            }
          />

          <CustomTypeDetailRow entity={entity} entityTypeId={entityTypeId} showLinks={showLinks} />

          <Table.Body.RowContent
            title={fieldLabels.projects}
            content={<GetProjects projects={entity.projects} createLinks={showLinks} />}
          />
        </Table.Body>
      </Table>
      <CustomTypeRenderer entity={entity} showLinks={showLinks} />
    </>
  );
};

interface InventoriesDetailProps extends EntityDetailProps<"inventoryItems"> {
  hideDirectChildrenTile?: boolean;
  hideSameTypeTile?: boolean;
  fullTitleOverride?: React.ReactNode;
  useQuickAdd?: boolean;
}

export const InventoriesDetail = ({
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entity,
  entityApi,
  hideDirectChildrenTile = false,
  hideSameTypeTile = false,
  fullTitleOverride,
  useQuickAdd,
  entityTypeId,
}: InventoriesDetailProps) => {
  const { route, session } = useContext(SessionContext);
  const isElnActive = useFeatureFlag("eln");
  let history = useHistory();

  const { data: validChildrenTypes } = useUnpaginateOrdered<CustomType, CustomTypeFilters>(
    customTypeConstants.resource,
    { entityTypes: ["InventoryItem"], parentTypeIds: entity.customType?.id ? [entity.customType.id] : [] },
    { enabled: !!entity.isHierarchyItem, staleTime: 120000, cacheTime: 120000 }
  );
  const canHaveChildItems = validChildrenTypes && validChildrenTypes.length > 0;
  const validChildrenTypesWithPermission = useMemo(
    () =>
      validChildrenTypes?.filter((t) =>
        t.hasRestrictedAddPermission
          ? !!session?.permissions.add_admin_inventory_items
          : !!session?.permissions.add_inventory_items
      ) ?? [],
    [validChildrenTypes, session]
  );
  const canCreateChildItems = useMemo(
    () =>
      !entity.isLocked &&
      !entity.isDeleted &&
      !entity.ancestors.some((a) => !!a.isLocked || !!a.isDeleted) &&
      !!validChildrenTypes?.some((t) =>
        t.hasRestrictedAddPermission
          ? !!session?.permissions.add_admin_inventory_items
          : !!session?.permissions.add_inventory_items
      ),
    [
      entity.isLocked,
      entity.isDeleted,
      entity.ancestors,
      validChildrenTypes,
      session?.permissions.add_admin_inventory_items,
      session?.permissions.add_inventory_items,
    ]
  );

  const { data: customType } = useEntityDetail<CustomType>(customTypeConstants.resource, entity.customType?.id ?? 0, {
    enabled: !!entity.customType?.id,
  });

  const [isQuickAddOpen, setIsQuickAddOpen] = useState<boolean>(false);

  const hasAddPermission = useMemo(() => {
    if (!customType) return false;
    if (customType.hasRestrictedAddPermission) {
      return !!session?.permissions.add_admin_inventory_items;
    } else {
      return !!session?.permissions.add_inventory_items;
    }
  }, [customType, session]);

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          fullTitleOverride={
            fullTitleOverride ?? (
              <EntityBreadcrumbs
                entity={entity}
                breadcrumbs={entity.ancestors.map((a) => ({
                  entity: a,
                  entityConstants,
                  ancestorLinkToOverride: entity.isHierarchyItem
                    ? () => {
                        return `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                          entity.rootCustomType?.id
                        }/hierarchy/${a.id}`;
                      }
                    : undefined,
                }))}
                entityConstants={entityConstants}
                indexEntityLabelOverride={entity.rootCustomType?.inventoryName}
                indexEntityLinkToOverride={
                  entity.isHierarchyItem
                    ? `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                        entity.rootCustomType?.id
                      }/hierarchy`
                    : `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${entity.customType?.id}/items`
                }
              />
            )
          }
          entity={entity}
          entityConstants={entityConstants}
          controls={
            <>
              {!!entity.customType && canHaveChildItems && (
                <>
                  {!useQuickAdd ? (
                    <AddCustomTypeEntity
                      entityConstants={entityConstants}
                      entityType="InventoryItem"
                      defaultFilters={{
                        entityTypes: ["InventoryItem"],
                        ids: validChildrenTypesWithPermission.map((t) => t.id),
                      }}
                      excludeFilters={{ parentTypeIds: () => false }}
                      btnCls="btn btn-soft-primary"
                      btnStr="Add child item"
                      iconOverride={hierarchyConstants.childIcon}
                      canCreateEntityPermission={canCreateChildItems}
                      disableBasicCreation
                      autoSelectSingleResult
                      onSelectCustomType={(customType) =>
                        history.push(
                          route(
                            `${getAddRoute(entityConstants.frontendIndexRoute)}?type=${customType.id}&parent=${
                              entity.id
                            }`
                          )
                        )
                      }
                    />
                  ) : (
                    <>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsQuickAddOpen(true);
                        }}
                        className="btn btn-soft-primary"
                        disabled={!canCreateChildItems}
                        title={
                          !!entity
                            ? `Add ${entityConstants.entitySingular} to ${entity.name}`
                            : `Add ${entityConstants.entitySingular}`
                        }
                      >
                        <LucideIcon name={hierarchyConstants.childItemIcon} /> Add child item
                      </Button>
                      <QuickAddWrapper showModal={isQuickAddOpen} setShowModal={setIsQuickAddOpen} onCreate={() => {}}>
                        {(quickAddProps) => (
                          <QuickAddInventoryItemForm
                            {...quickAddProps}
                            initialValues={{
                              parent: entity,
                              customType:
                                !!validChildrenTypesWithPermission && validChildrenTypesWithPermission.length === 1
                                  ? validChildrenTypesWithPermission[0]
                                  : undefined,
                            }}
                            customTypeFilters={{
                              entityTypes: ["InventoryItem"],
                              ids: validChildrenTypesWithPermission.map((t) => t.id),
                            }}
                            subtitle={
                              !!entity
                                ? entity.name.length > 30
                                  ? `Parent: ${entity.name.slice(0, 30)}...`
                                  : `Parent: ${entity.name}`
                                : undefined
                            }
                            onCreate={(createdEntity) => {
                              !!createdEntity &&
                                history.push(
                                  createdEntity.isHierarchyItem
                                    ? `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                                        createdEntity.rootCustomType?.id
                                      }/hierarchy/${createdEntity.id}`
                                    : `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                                        createdEntity.customType?.id
                                      }/items/${createdEntity.id}`
                                );
                              setIsQuickAddOpen(false);
                            }}
                            onCancel={() => setIsQuickAddOpen(false)}
                          />
                        )}
                      </QuickAddWrapper>
                    </>
                  )}
                </>
              )}
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />

              <EntityRestoreButton
                entity={entity}
                entityConstants={entityConstants}
                entityApi={entityApi}
                permissions={permissions}
              />
            </>
          }
          controlsDropdown={
            <>
              <li>
                <EntityCloneButton
                  entity={entity}
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                  disabledOverride={
                    !permissions.canCreate ||
                    entity.ancestors.some((a) => !!a.isLocked || !!a.isDeleted) ||
                    !hasAddPermission
                  }
                />
              </li>
              <li>
                <GenericEntityExportButton entity={entity} entityTypeId={entityTypeId} />
              </li>
              <EntityComplianceDropdown
                entity={entity}
                entityTypeId={entityTypeId}
                propOverrides={{
                  GenericEntitySignButtonProps: {
                    componentProps: {
                      overrideRedirectUrl: (entity) =>
                        entity.isHierarchyItem
                          ? `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                              entity.rootCustomType?.id
                            }/hierarchy/${entity.id}`
                          : `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                              entity.customType?.id
                            }/items/${entity.id}`,
                    },
                  },
                  GenericEntityUnlockButtonProps: {
                    componentProps: {
                      overrideRedirectUrl: (entity) =>
                        entity.isHierarchyItem
                          ? `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                              entity.rootCustomType?.id
                            }/hierarchy/${entity.id}`
                          : `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${
                              entity.customType?.id
                            }/items/${entity.id}`,
                    },
                  },
                }}
                showSignButton
              />
              <li className="divider" />
              <li>
                <EntityDeleteButton entityTypeId={entityTypeId} entity={entity} />
              </li>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <InventoryItemsDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
        />
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          {!hideDirectChildrenTile && !!entity.isHierarchyItem && (
            <InventoriesTile
              title="Direct children"
              defaultFilters={{
                sortBy: [{ property: "NAME", sortDirection: "ASC" }],
              }}
              showCustomTypeSelectWidget
              parentInventoryItem={entity}
              isFolded
              foldable
            />
          )}

          {!hideSameTypeTile && !!entity.isHierarchyItem && (
            <InventoriesTile
              title="Items of the same type"
              defaultFilters={{
                customTypeIds: entity.customType?.id ? [entity.customType.id] : undefined,
                excludeIds: [entity.id],
              }}
              excludeFilters={{ customTypeIds: () => false }}
              isFolded
              foldable
              controlsFixed={() => (
                <div className="flex align-center gap-5">
                  <button
                    disabled={!entity.customType?.id}
                    className="btn btn-default"
                    onClick={() =>
                      !!entity.customType?.id &&
                      history.push(
                        `${route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}/${entity.customType.id}/items`
                      )
                    }
                  >
                    <LucideIcon name="table" /> View table
                  </button>
                </div>
              )}
            />
          )}

          <CustomFieldReferencesTile
            entityType="InventoryItem"
            entity={entity}
            labNotebookEntryFilters={
              isElnActive
                ? {
                    inventoryItemReferenceIds: [entity.id],
                    sortBy: [{ property: "MODIFIED_ON", sortDirection: "DESC" }],
                  }
                : undefined
            }
          />
        </EntitySidebarTileAutosorter>
      }
      sidebarMinSize={33}
    />
  );
};
